import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { includesFolded } from "../utils/text";
import {
  IconStaff, IconPlus, IconLoader, IconAlert, IconSearch, IconClock,
} from "../components/Icons";

// ---- Types ----
interface Employee {
  id: string;
  full_name: string | null;
  display_name: string;
  role: EmployeeRole;
  is_active: boolean;
  allowed_branch_ids: string[] | null;
  created_at: string | null;
  last_login_at: string | null;
  last_activity_at: string | null;
}

type EmployeeRole = "employee" | "supervisor" | "manager";
interface Branch {
  id: string;
  name: string;
  code: string;
}
interface AttendanceSession {
  id: string;
  employee_id: string;
  employee_name: string;
  branch_id: string;
  branch_name: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  note: string | null;
}

type Tab = "employees" | "shifts";

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTimeAgo(iso: string | null, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("staff.justNow");
  if (mins < 60) return t("staff.minutesAgo", { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("staff.hoursAgo", { count: hours });
  return t("staff.daysAgo", { count: Math.floor(hours / 24) });
}

export default function Staff() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canWrite = can(user, "staff", "write");
  const canAttendance = can(user, "attendance");
  const [tab, setTab] = useState<Tab>("employees");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [emps, brs, sess] = await Promise.all([
        api.get<Employee[]>("/staff/employees?limit=500").then(r => r.data),
        api.get<Branch[]>("/branches?limit=500").then(r => r.data),
        canAttendance ? api.get<AttendanceSession[]>("/staff/attendance?limit=50").then(r => r.data) : Promise.resolve([]),
      ]);
      setEmployees(emps);
      setBranches(brs);
      setSessions(sess);
    } catch (e) {
      setErr(apiError(e, t("staff.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const branchName = (id: string | null) => {
    if (!id) return t("staff.allBranches");
    return branches.find(b => b.id === id)?.name || "—";
  };

  const filteredEmployees = employees.filter(e => {
    if (!search) return true;
    return includesFolded(e.display_name, search)
      || includesFolded(e.full_name || "", search);
  });

  async function saveEmployee(data: Record<string, unknown>) {
    setErr(null);
    try {
      if (editing) {
        await api.put(`/staff/employees/${editing.id}`, data);
      } else {
        await api.post("/staff/employees", data);
      }
      setShowForm(false);
      setEditing(null);
      await loadAll();
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setErr(status === 409
        ? t("staff.pinTaken")
        : t("staff.saveError"));
    }
  }

  async function deleteEmployee(id: string) {
    if (!confirm(t("staff.deactivateConfirm"))) return;
    try {
      await api.delete(`/staff/employees/${id}`);
      await loadAll();
    } catch (e) {
      setErr(apiError(e, t("staff.deleteError")));
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("staff.title")}</h1>
          <p>{t("staff.subtitle")}</p>
        </div>
        {tab === "employees" && canWrite && (
          <button className="primary" onClick={() => { setEditing(null); setShowForm(true); }}>
            <IconPlus size={18} /> {t("staff.newEmployee")}
          </button>
        )}
      </div>

      {err && (
        <div className="card" style={{
          padding: "12px 16px", marginBottom: 20,
          background: "var(--red-dim)", color: "var(--red)",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
        {([
          { id: "employees" as Tab, label: `${t("staff.tabEmployees")} (${employees.length})` },
          ...(canAttendance ? [{ id: "shifts" as Tab, label: t("staff.tabShifts", { count: sessions.filter(s => !s.ended_at).length }) }] : []),
        ]).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 18px", border: "none", background: "transparent",
              color: tab === t.id ? "var(--accent)" : "var(--muted)",
              fontWeight: tab === t.id ? 600 : 500, fontSize: 14,
              borderBottom: tab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
              cursor: "pointer", marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ---- Employees tab ---- */}
      {tab === "employees" && (
        <>
          {showForm && (
            <EmployeeForm
              employee={editing}
              branches={branches}
              onSave={saveEmployee}
              onCancel={() => { setShowForm(false); setEditing(null); }}
            />
          )}
          <div style={{ position: "relative", marginBottom: 16, maxWidth: 360 }}>
            <IconSearch size={18} className="muted" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
            <input
              placeholder={t("staff.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 38, width: "100%" }}
            />
          </div>
          {filteredEmployees.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: "center" }}>
              <IconStaff size={48} className="muted" />
              <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{t("staff.empty")}</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8 }}>{t("staff.emptyHint")}</p>
            </div>
          ) : (
            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th>{t("staff.colFullName")}</th>
                    <th>{t("staff.colDisplayName")}</th>
                    <th>{t("users.role")}</th>
                    <th>{t("staff.colBranches")}</th>
                    <th>{t("users.colCreated")}</th>
                    <th>{t("users.colLastLogin")}</th>
                    <th>{t("staff.colActivity")}</th>
                    <th>{t("common.status")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(e => (
                    <tr key={e.id}>
                      <td>{e.full_name || "—"}</td>
                      <td style={{ fontWeight: 600 }}>{e.display_name}</td>
                      <td style={{ fontSize: 13 }}><span className="badge">{t(`staff.role.${e.role}`)}</span></td>
                      <td style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {e.allowed_branch_ids && e.allowed_branch_ids.length > 0
                          ? e.allowed_branch_ids.map(bid => branchName(bid)).join(", ")
                          : t("staff.allBranches")}
                      </td>
                      <td style={{ color: "var(--muted)", fontSize: 13 }}>{e.created_at ? new Date(e.created_at).toLocaleDateString("cs-CZ") : "—"}</td>
                      <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatTimeAgo(e.last_login_at, t)}</td>
                      <td style={{ color: "var(--muted)", fontSize: 13 }}>{formatTimeAgo(e.last_activity_at, t)}</td>
                      <td><span className={e.is_active ? "badge badge-green" : "badge badge-red"}>{e.is_active ? t("common.active") : t("common.inactive")}</span></td>
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          {canWrite && (
                          <>
                          <button className="ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => { setEditing(e); setShowForm(true); }}>{t("common.edit")}</button>
                          <button className="ghost" style={{ padding: "4px 10px", fontSize: 12, color: "var(--red)" }} onClick={() => deleteEmployee(e.id)}>{t("common.delete")}</button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ---- Shifts tab ---- */}
      {tab === "shifts" && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{t("staff.shiftsHistory")}</h3>
              <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 2 }}>{t("staff.last50")}</p>
            </div>
            <IconClock size={20} className="muted" />
          </div>
          {sessions.length === 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
              {t("staff.noShifts")}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("staff.colEmployee")}</th>
                  <th>{t("staff.colBranch")}</th>
                  <th>{t("staff.colStart")}</th>
                  <th>{t("staff.colEnd")}</th>
                  <th>{t("staff.colDuration")}</th>
                  <th>{t("common.status")}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => {
                  const duration = s.duration_seconds
                    ? s.duration_seconds
                    : s.ended_at
                      ? Math.floor((new Date(s.ended_at).getTime() - new Date(s.started_at).getTime()) / 1000)
                      : Math.floor((Date.now() - new Date(s.started_at).getTime()) / 1000);
                  return (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.employee_name}</td>
                      <td>{s.branch_name}</td>
                      <td style={{ fontSize: 13 }}>{new Date(s.started_at).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td style={{ fontSize: 13 }}>
                        {s.ended_at
                          ? new Date(s.ended_at).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </td>
                      <td><span className="badge badge-green">{formatDuration(duration)}</span></td>
                      <td>
                        <span className={s.ended_at ? "badge" : "badge badge-yellow"}>
                          {s.ended_at ? t("staff.closed") : t("staff.open")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function EmployeeForm({ employee, branches, onSave, onCancel }: {
  employee: Employee | null;
  branches: Branch[];
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(employee?.full_name || "");
  const [displayName, setDisplayName] = useState(employee?.display_name || "");
  const [role, setRole] = useState<EmployeeRole>(employee?.role || "employee");
  const [pin, setPin] = useState("");
  const [isActive, setIsActive] = useState(employee?.is_active ?? true);
  const [selectedBranches, setSelectedBranches] = useState<string[]>(employee?.allowed_branch_ids || []);

  function toggleBranch(id: string) {
    setSelectedBranches(prev =>
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const data: Record<string, unknown> = {
      full_name: fullName || null,
      display_name: displayName,
      role,
      is_active: isActive,
      allowed_branch_ids: selectedBranches.length > 0 ? selectedBranches : null,
    };
    if (!employee) {
      data.pin = pin;
    } else if (pin) {
      data.new_pin = pin;
    }
    onSave(data);
  }

  return (
    <div className="card" style={{ padding: 24, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>{employee ? t("staff.editEmployee") : t("staff.newEmployee")}</h3>
      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
        <div>
          <label>{t("staff.colFullName")}</label>
          <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t("staff.fullNamePh")} />
        </div>
        <div>
          <label>{t("users.role")} *</label>
          <select value={role} onChange={e => setRole(e.target.value as EmployeeRole)} style={{ width: "100%" }}>
            <option value="employee">{t("staff.role.employee")}</option>
            <option value="supervisor">{t("staff.role.supervisor")}</option>
            <option value="manager">{t("staff.role.manager")}</option>
          </select>
        </div>
        <div>
          <label>{t("staff.displayNameLabel")} *</label>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={t("staff.displayNamePh")} required />
        </div>
        <div>
          <label>{employee ? t("staff.newPin") : "PIN *"}</label>
          <input type="password" inputMode="numeric" pattern="[0-9]*" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••" required={!employee} minLength={4} maxLength={32} />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} /> {t("common.active")}
          </label>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label>{t("staff.allowedBranches")}</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {branches.map(b => (
              <label key={b.id} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border)", cursor: "pointer", fontSize: 13,
                background: selectedBranches.includes(b.id) ? "var(--accent-dim)" : "var(--bg-elevated)",
              }}>
                <input
                  type="checkbox"
                  checked={selectedBranches.includes(b.id)}
                  onChange={() => toggleBranch(b.id)}
                  style={{ margin: 0 }}
                />
                {b.name}
              </label>
            ))}
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
          <button type="submit" className="primary">{employee ? t("customers.saveChanges") : t("staff.createEmployee")}</button>
          <button type="button" className="ghost" onClick={onCancel}>{t("common.cancel")}</button>
        </div>
      </form>
    </div>
  );
}
